import React, { Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClientInstance } from "@/lib/query-client";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  Navigate,
} from "react-router-dom";

import MissingRoute from "./lib/MissingRoute.jsx";
import ErrorBoundary from "./lib/ErrorBoundary";
import ConsolePrivacyGuard from "./lib/ConsolePrivacyGuard";
import { AuthProvider, useAuth } from "@/lib/AuthContext";
import { LanguageProvider, useLanguage } from "@/lib/LanguageContext";
import { ThemeProvider } from "@/lib/ThemeProvider";
import { OfflineProvider } from "@/lib/OfflineContext";

import UserNotRegisteredError from "@/components/UserNotRegisteredError";
import OfflineIndicator from "@/components/OfflineIndicator";
import FormAttributeDefaults from "@/components/layout/FormAttributeDefaults";
import HardcodedTextLocalizer from "@/components/localization/HardcodedTextLocalizer";
const PWAInstallBanner = React.lazy(() => import("@/components/pwa/PWAInstallBanner"));
const WelcomeModal = React.lazy(() => import("@/components/WelcomeModal"));
const NewUserTour = React.lazy(() => import("@/components/onboarding/NewUserTour"));
import { useSessionTimer } from "@/hooks/useSessionTimer";

const lazyWithRetry = (importer, retries = 6, delayMs = 1500) => React.lazy(async () => {
  let lastError;
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      const module = await importer();
      sessionStorage.removeItem("marstick_dynamic_import_reloaded");
      return module;
    } catch (error) {
      lastError = error;
      if (attempt === retries) break;
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }

  const message = String(lastError?.message || "");
  const shouldReload = /Failed to fetch dynamically imported module|Importing a module script failed|Loading chunk/i.test(message);
  if (shouldReload && sessionStorage.getItem("marstick_dynamic_import_reloaded") !== "true") {
    sessionStorage.setItem("marstick_dynamic_import_reloaded", "true");
    window.location.reload();
    return new Promise(() => {});
  }

  throw lastError;
});

const AppLayout = React.lazy(() => import("./components/layout/AppLayout"));
const AdminLayout = lazyWithRetry(() => import("./components/admin/AdminLayout"));
import SeoManager from "./components/seo/SeoManager";
import RouteLoading from "@/components/layout/RouteLoading";
import { SUPPORTED_LANGS, learningArticleUrl, learningCategoryUrl } from "@/lib/learningUrls";
import { SUPPORTED_LANGUAGES, localizedRoutes, getLocalizedPath, getSamePageLocalizedPath, getRouteKeyFromPath } from "@/lib/i18nRoutes";
import { detectLanguage } from "@/lib/languageDetector";

// === PUBLIC PAGES ===
const PublicLayout = lazyWithRetry(() => import("./components/public/PublicLayout"));
const PublicHome = lazyWithRetry(() => import("./pages/public/PublicHome"));
const Affiliate = React.lazy(() => import("./pages/Affiliate"));
const ReferralAttributionCapture = React.lazy(() => import("./components/affiliate/ReferralAttributionCapture"));
const Dashboard = React.lazy(() => import("./pages/Dashboard"));
const NavigationPage = React.lazy(() => import("./pages/Navigation"));
const CloudTypes = React.lazy(() => import("./pages/CloudTypes"));
const CloudDetail = React.lazy(() => import("./pages/CloudDetail.jsx"));
const WindGuide = React.lazy(() => import("./pages/WindGuide.jsx"));
const BeaufortGuide = React.lazy(() => import("./pages/BeaufortGuide.jsx"));
const CloudCoverageGuide = React.lazy(() => import("./pages/CloudCoverageGuide.jsx"));
const AirPressureGuide = React.lazy(() => import("./pages/AirPressureGuide.jsx"));
const UVIndexGuide = React.lazy(() => import("./pages/UVIndexGuide.jsx"));
const RelativeHumidityGuide = React.lazy(() => import("./pages/RelativeHumidityGuide.jsx"));
const SeaStateGuide = React.lazy(() => import("./pages/SeaStateGuide.jsx"));
const VisibilityGuide = React.lazy(() => import("./pages/VisibilityGuide.jsx"));
const RainGuide = React.lazy(() => import("./pages/RainGuide.jsx"));
const SailingDecisionGuide = React.lazy(() => import("./pages/SailingDecisionGuide.jsx"));
const SunTimesGuide = React.lazy(() => import("./pages/SunTimesGuide.jsx"));
const TemperatureGuide = React.lazy(() => import("./pages/TemperatureGuide.jsx"));
const TodayMarineForecastGuide = React.lazy(() => import("./pages/TodayMarineForecastGuide.jsx"));
const WeatherGustsGuide = React.lazy(() => import("./pages/WeatherGustsGuide.jsx"));
const WeatherScoreGuide = React.lazy(() => import("./pages/WeatherScoreGuide.jsx"));
const CloudTypeGuide = React.lazy(() => import("./pages/CloudTypeGuide.jsx"));
const CloudNameGuide = React.lazy(() => import("./pages/CloudNameGuide.jsx"));
const MaxWindGuide = React.lazy(() => import("./pages/MaxWindGuide.jsx"));
const MaxWavesGuide = React.lazy(() => import("./pages/MaxWavesGuide.jsx"));
const AirHighGuide = React.lazy(() => import("./pages/AirHighGuide.jsx"));
const WeatherDailyForecastGuide = React.lazy(() => import("./pages/WeatherDailyForecastGuide.jsx"));
const TideGuide = React.lazy(() => import("./pages/TideGuide.jsx"));
const WeatherAlertsGuide = React.lazy(() => import("./pages/WeatherAlertsGuide.jsx"));
const WeatherTrendsGuide = React.lazy(() => import("./pages/WeatherTrendsGuide.jsx"));
const StormApproachTrendGuide = React.lazy(() => import("./pages/StormApproachTrendGuide.jsx"));
const SafetyChecklistGuide = React.lazy(() => import("./pages/SafetyChecklistGuide.jsx"));
const SafetyEquipmentGuide = React.lazy(() => import("./pages/SafetyEquipmentGuide.jsx"));
const DownloadsHub = React.lazy(() => import("./pages/DownloadsHub.jsx"));
const Radar = React.lazy(() => import("./pages/Radar"));
const MyFleet = React.lazy(() => import("./pages/MyFleet"));
const Maintenance = React.lazy(() => import("./pages/Maintenance"));
const OfflineNavigation = React.lazy(() => import("./pages/OfflineNavigation"));
const Settings = React.lazy(() => import("./pages/Settings"));
const Metrics = React.lazy(() => import("./pages/Metrics"));
const Logbook = React.lazy(() => import("./pages/Logbook"));
const HelpCenter = React.lazy(() => import("./pages/HelpCenter"));
const TripPlanner = React.lazy(() => import("./pages/TripPlanner"));
const Onboarding = React.lazy(() => import("./pages/Onboarding"));
const Billing = React.lazy(() => import("./pages/Billing"));
const SubscriptionSuccess = React.lazy(() => import("./pages/SubscriptionSuccess"));
const PayPalReturn = React.lazy(() => import("./pages/PayPalReturn"));
const Landing = React.lazy(() => import("./pages/Landing"));
const Privacy = React.lazy(() => import("./pages/Privacy"));
const TermsAndConditions = React.lazy(() => import("./pages/TermsAndConditions"));
const CookiePolicy = React.lazy(() => import("./pages/CookiePolicy"));
const Contact = React.lazy(() => import("./pages/Contact"));
const Community = React.lazy(() => import("./pages/Community"));
const Advertise = lazyWithRetry(() => import("./pages/Advertise"));
const TopicPage = React.lazy(() => import("./pages/education/TopicPage"));
const LearningArticleRouteGuard = React.lazy(() => import("./components/learning/LearningArticleRouteGuard"));
const LocalizedPublicPage = lazyWithRetry(() => import("./pages/LocalizedPublicPage"));
const PortEntryPack = React.lazy(() => import("./pages/PortEntryPack"));
const PublicIndexPage = lazyWithRetry(() => import("./pages/public/PublicIndexPage"));
const NearMePage = React.lazy(() => import("./pages/public/NearMePage"));
const ToolPage = React.lazy(() => import("./pages/public/ToolPage"));
const WireIndex = React.lazy(() => import("./pages/public/WireIndex"));
const WireArticlePage = React.lazy(() => import("./pages/public/WireArticlePage"));
const ProfilePage = lazyWithRetry(() => import("./pages/public/ProfilePage"));
const MobileMenuPage = React.lazy(() => import("./pages/public/MobileMenuPage"));
const PublicLearningArticlePage = React.lazy(() => import("./pages/public/PublicLearningArticlePage"));
const PublicMarinaDetailPage = React.lazy(() => import("./pages/public/PublicMarinaDetailPage"));
const MarinaLegacyRedirect = React.lazy(() => import("./components/marinas/MarinaLegacyRedirect"));
const SearchPage = React.lazy(() => import("./pages/public/SearchPage"));
const TrustPolicyPage = React.lazy(() => import("./pages/public/TrustPolicyPage"));
const InstallGuidePage = React.lazy(() => import("./pages/public/InstallGuidePage"));
const ExtensionsPage = React.lazy(() => import("./pages/public/ExtensionsPage"));
const ExtensionsEnterprisePage = React.lazy(() => import("./pages/public/ExtensionsEnterprisePage"));
const DailyBriefingPage = React.lazy(() => import("./pages/public/DailyBriefingPage"));
const SeoGrowthPage = React.lazy(() => import("./pages/public/SeoGrowthPage"));
const AISCollisionCenter = React.lazy(() => import("./pages/AISCollisionCenter"));
const BoatDocumentVault = React.lazy(() => import("./pages/BoatDocumentVault"));
const RoutesHub = React.lazy(() => import("./pages/RoutesHub"));
const RegulationsHub = React.lazy(() => import("./pages/RegulationsHub"));
const GlossaryHub = React.lazy(() => import("./pages/GlossaryHub"));
const RouteDetailPage = React.lazy(() => import("./pages/RouteDetailPage"));
const WeatherSpotPage = React.lazy(() => import("./pages/WeatherSpotPage"));
const RegulationDetailPage = React.lazy(() => import("./pages/RegulationDetailPage"));
const GlossaryTermPage = React.lazy(() => import("./pages/GlossaryTermPage"));
const MarstickBusinessTier = React.lazy(() => import("./pages/MarstickBusinessTier"));
const FleetOperations = React.lazy(() => import("./pages/FleetOperations"));

// === ADMIN PAGES ===
const AdminDashboard = React.lazy(() => import("./pages/admin/AdminDashboard"));
const AdminNotifications = React.lazy(() => import("./pages/admin/AdminNotifications"));
const EducationalContentManager = React.lazy(() => import("./pages/admin/EducationalContentManager"));
const AdminAnalytics = React.lazy(() => import("./pages/admin/AdminAnalytics"));
const AdManager = React.lazy(() => import("./pages/admin/AdManager"));
const AdSenseTest = React.lazy(() => import("./pages/admin/AdSenseTest"));
const AdPerformanceDashboard = React.lazy(() => import("./pages/admin/AdPerformanceDashboard"));
const SystemHealth = lazyWithRetry(() => import("./pages/admin/SystemHealth"));
const UserManagement = React.lazy(() => import("./pages/admin/UserManagement"));
const SEODashboard = React.lazy(() => import("./pages/admin/SEODashboard"));
const SeoMetaManager = React.lazy(() => import("./pages/admin/SeoMetaManager"));
const SeoMetaSettings = React.lazy(() => import("./pages/admin/SeoMetaSettings"));
const SitemapVerifier = React.lazy(() => import("./pages/admin/SitemapVerifier"));
const PremiumSegments = React.lazy(() => import("./pages/admin/PremiumSegments"));
const WelcomeContentEditor = React.lazy(() => import("./pages/admin/WelcomeContentEditor"));
const LearningContentManager = lazyWithRetry(() => import("./pages/admin/LearningContentManager"));
const ContentQualityDashboard = React.lazy(() => import("./pages/admin/ContentQualityDashboard"));
const GlobalSearchTerms = React.lazy(() => import("./pages/admin/GlobalSearchTerms"));
const TrackingSettings = React.lazy(() => import("./pages/admin/TrackingSettings"));
const TranslationCenter = React.lazy(() => import("./pages/admin/TranslationCenter"));
const AIBusinessIntelligence = React.lazy(() => import("./pages/admin/AIBusinessIntelligence"));
const SupportChat = React.lazy(() => import("./pages/admin/SupportChat"));
const PaymentSolutions = React.lazy(() => import("./pages/admin/PaymentSolutions"));
const PaymentHistory = React.lazy(() => import("./pages/admin/PaymentHistory"));
const ChurnRiskMonitor = React.lazy(() => import("./pages/admin/ChurnRiskMonitor"));
const NavigationVisibility = React.lazy(() => import("./pages/admin/NavigationVisibility"));
const RevenueGrowth = React.lazy(() => import("./pages/admin/RevenueGrowth"));
const AdvertisingContentEditor = React.lazy(() => import("./pages/admin/AdvertisingContentEditor"));
const BackupExport = React.lazy(() => import("./pages/admin/BackupExport"));
const SocialMediaManager = React.lazy(() => import("./pages/admin/SocialMediaManager"));
const QAReports = React.lazy(() => import("./pages/admin/QAReports"));
const YouTubeScriptGenerator = React.lazy(() => import("./pages/admin/YouTubeScriptGenerator"));
const BacklinkManager = React.lazy(() => import("./pages/admin/BacklinkManager"));
const MarinaDuplicateAgent = React.lazy(() => import("./pages/admin/MarinaDuplicateAgent"));
const AdminToolPage = React.lazy(() => import("./pages/admin/AdminToolPage"));
const BrowserExtensions = lazyWithRetry(() => import("./pages/admin/BrowserExtensions"));
const AdminAffiliateProgram = React.lazy(() => import("./pages/admin/AffiliateProgram"));

// ─────────────────────────────────────────────────────────────────────────────
// LearningCategoryRedirect
// Handles bare /learning/:category URLs (e.g. /learning/navigation-positioning)
// by redirecting them to the canonical /en/learning-center?category=... URL.
// Fixes Google Search Console "Soft 404" / "Page with redirect" issues by
// emitting a true browser-level replace, which Googlebot's renderer treats
// as a 301 client-side redirect.
// ─────────────────────────────────────────────────────────────────────────────
function LearningCategoryRedirect() {
  const { category } = useParams();
  const location = useLocation();
  const detected = detectLanguage({ pathname: location.pathname, search: location.search });
  return (
    <Navigate
      to={learningCategoryUrl({ lang: detected, category })}
      replace
    />
  );
}

function LearningArticleRedirect() {
  const { lang, category, slug } = useParams();
  const location = useLocation();
  const { pathname, search } = location;
  const safeLang = SUPPORTED_LANGS.includes(lang) ? lang : detectLanguage({ pathname, search });

  React.useEffect(() => {
    if (import.meta.env.DEV) console.warn(`[learning-url] legacy /learning/ hit: ${pathname}`);
  }, [pathname]);

  return (
    <Navigate
      to={learningArticleUrl({ lang: safeLang, category, slug })}
      replace
    />
  );
}

function LearnRedirect() {
  const { lang = "en", topic, articleSlug } = useParams();
  const safeLang = SUPPORTED_LANGS.includes(lang) ? lang : "en";
  const path = articleSlug
    ? `/${safeLang}/learning-center/${topic}/${articleSlug}`
    : topic
      ? `/${safeLang}/learning-center/${topic}`
      : `/${safeLang}/learning-center`;

  return <Navigate to={path} replace />;
}

function BrowserLanguageNavigate({ routeKey }) {
  const location = useLocation();
  const detected = detectLanguage({ pathname: location.pathname, search: location.search });
  return <Navigate to={`${getLocalizedPath(routeKey, detected)}${location.search || ""}${location.hash || ""}`} replace />;
}

function LocalizedLegacyRoute({ routeKey, children }) {
  const { lang } = useParams();
  const location = useLocation();
  const safeLang = SUPPORTED_LANGS.includes(lang) ? lang : detectLanguage({ pathname: location.pathname, search: location.search });
  const localizedPath = getLocalizedPath(routeKey, safeLang);
  const currentPath = location.pathname.replace(/\/+$/, "") || "/";

  if (safeLang !== "en" && localizedPath !== "/404" && currentPath !== localizedPath) {
    return <Navigate to={`${localizedPath}${location.search || ""}${location.hash || ""}`} replace />;
  }

  return children;
}

function LocalizedRouteRedirect({ routeKey }) {
  const { lang } = useParams();
  const location = useLocation();
  const safeLang = SUPPORTED_LANGS.includes(lang) ? lang : detectLanguage({ pathname: location.pathname, search: location.search });
  return <Navigate to={`${getLocalizedPath(routeKey, safeLang)}${location.search || ""}${location.hash || ""}`} replace />;
}

function PortsLegacyRedirect() {
  const { lang, country, port } = useParams();
  const base = lang && SUPPORTED_LANGS.includes(lang) ? `/${lang}/marinas` : "/marinas";
  const path = country ? `${base}/${country}${port ? `/${port}` : ""}` : base;
  return <Navigate to={path} replace />;
}

const LocalizedNavigationRoute = () => <SafeRoute><NavigationPage /></SafeRoute>;


// ====================== ADMIN LAYOUT ======================
const SafeRoute = ({ children }) => <ErrorBoundary>{children}</ErrorBoundary>;

const ScrollToTop = () => {
  const { pathname, search } = useLocation();

  React.useEffect(() => {
    if (window.location.hash) {
      const target = document.querySelector(window.location.hash);
      if (target) {
        target.scrollIntoView({ block: "start", behavior: "auto" });
        return;
      }
    }

    const pendingScrollKey = sessionStorage.getItem("marstick_mobile_tab_pending_scroll_key");
    if (pendingScrollKey && window.matchMedia?.("(max-width: 767px)").matches) {
      sessionStorage.removeItem("marstick_mobile_tab_pending_scroll_key");
      const top = Number(sessionStorage.getItem(pendingScrollKey) || 0);
      requestAnimationFrame(() => window.scrollTo({ top, left: 0, behavior: "auto" }));
      return;
    }

    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname, search]);

  return null;
};

const LANGUAGE_EXEMPT_PATH = /^\/(admin|api|_functions|assets|favicon|manifest|sw\.js|robots\.txt|sitemap\.xml|ads\.txt|ads\.xml)(\/|$)/;

const useDeferredIdleRender = (delayMs = 1800) => {
  const [ready, setReady] = React.useState(false);

  React.useEffect(() => {
    const schedule = window.requestIdleCallback
      ? window.requestIdleCallback(() => setReady(true), { timeout: delayMs + 2200 })
      : window.setTimeout(() => setReady(true), delayMs);

    return () => {
      if (window.cancelIdleCallback && typeof schedule === "number") window.cancelIdleCallback(schedule);
      else window.clearTimeout(schedule);
    };
  }, [delayMs]);

  return ready;
};

const LanguagePreferenceNavigator = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { language } = useLanguage();

  React.useEffect(() => {
    const explicit = localStorage.getItem("marstick_locale_explicit") === "true";
    const preferred = localStorage.getItem("marstick_locale") || language;
    if (!explicit || !SUPPORTED_LANGUAGES.includes(preferred) || LANGUAGE_EXEMPT_PATH.test(location.pathname)) return;

    const pathParts = location.pathname.split("/").filter(Boolean);
    const pathLang = pathParts[0];
    if (SUPPORTED_LANGUAGES.includes(pathLang)) return;
    const routeStart = pathParts[0];
    if (pathParts.length && !getRouteKeyFromPath(`/${routeStart || ""}`)) return;

    const nextPath = getSamePageLocalizedPath(location.pathname, preferred);
    if (nextPath && nextPath !== location.pathname && nextPath !== "/404") {
      navigate(`${nextPath}${location.search || ""}${location.hash || ""}`, { replace: true });
    }
  }, [language, location.pathname, location.search, location.hash, navigate]);

  return null;
};

// =========================================================

const AuthenticatedApp = () => {
  const { isLoadingAuth, isLoadingPublicSettings, authError, authResolved } = useAuth();
  const location = useLocation();
  const isNavigationPage = /\/navigation\/?$/.test(location.pathname);
  const isAuthenticatedArea = /^\/(admin|dashboard|boats|settings|billing|onboarding|trips|metrics|offline)(\/|$)/.test(location.pathname);
  const canRenderPublicRouteBeforeAuth = !isAuthenticatedArea;
  const showOnboardingOverlays = isAuthenticatedArea && !location.pathname.startsWith("/admin") && !location.pathname.startsWith("/offline") && !isNavigationPage;
  const showDeferredUi = useDeferredIdleRender();
  useSessionTimer();

  // REMOVED: robots meta override that conflicted with SeoManager.
  // SeoManager correctly handles noindex for admin/auth/ghost paths.
  // This effect was overwriting those decisions with 'index,follow' for
  // all non-admin paths, causing auth-only pages (/billing, /settings,
  // /trips, etc.) to be marked as indexable in Google Search Console.

  if (!canRenderPublicRouteBeforeAuth && (isLoadingPublicSettings || isLoadingAuth || !authResolved)) {
    return (
      <>
        <SeoManager />
        <div className="fixed inset-0 flex items-center justify-center bg-background">Loading...</div>
      </>
    );
  }

  if (authError) {
    if (authError.type === "user_not_registered") return <UserNotRegisteredError />;
    if (authError.type === "auth_required") {
      // Allow the app to remain browsable without forcing a login redirect.
    }
  }

  return (
    <>
      <OfflineIndicator />
      {showDeferredUi && (
        <Suspense fallback={null}>
          <PWAInstallBanner />
          {showOnboardingOverlays && (
            <>
              <WelcomeModal />
              <NewUserTour />
            </>
          )}
        </Suspense>
      )}
      <SeoManager />
      <Suspense fallback={<RouteLoading />}>
      <Routes>
        {/* Public SEO-first routes */}
        <Route path="/:lang" element={<PublicLayout />}>
          <Route index element={<PublicHome />} />
          <Route path="weather" element={<PublicIndexPage pageKey="weather" />} />
          <Route path="daily-briefing" element={<DailyBriefingPage />} />
          <Route path="install" element={<InstallGuidePage />} />
          <Route path="extensions" element={<ExtensionsPage />} />
          <Route path="extensions/enterprise" element={<ExtensionsEnterprisePage />} />
          <Route path="extensions/:browserSlug" element={<ExtensionsPage />} />
          <Route path="weather/wind-guide" element={<WindGuide />} />
          <Route path="weather/beaufort-guide" element={<BeaufortGuide />} />
          <Route path="weather/cloud-coverage-guide" element={<CloudCoverageGuide />} />
          <Route path="weather/air-pressure-guide" element={<AirPressureGuide />} />
          <Route path="weather/uv-index-guide" element={<UVIndexGuide />} />
          <Route path="weather/relative-humidity-guide" element={<RelativeHumidityGuide />} />
          <Route path="weather/sea-state-guide" element={<SeaStateGuide />} />
          <Route path="weather/visibility-guide" element={<VisibilityGuide />} />
          <Route path="weather/rain-guide" element={<RainGuide />} />
          <Route path="weather/sailing-decision-guide" element={<SailingDecisionGuide />} />
          <Route path="weather/sun-times-guide" element={<SunTimesGuide />} />
          <Route path="weather/temperature-guide" element={<TemperatureGuide />} />
          <Route path="weather/today-marine-forecast-guide" element={<TodayMarineForecastGuide />} />
          <Route path="weather/gusts-guide" element={<WeatherGustsGuide />} />
          <Route path="weather/score-guide" element={<WeatherScoreGuide />} />
          <Route path="weather/cloud-type-guide" element={<CloudTypeGuide />} />
          <Route path="weather/cloud-name-guide" element={<CloudNameGuide />} />
          <Route path="weather/max-wind-guide" element={<MaxWindGuide />} />
          <Route path="weather/max-waves-guide" element={<MaxWavesGuide />} />
          <Route path="weather/air-high-guide" element={<AirHighGuide />} />
          <Route path="weather/daily-forecast-guide" element={<WeatherDailyForecastGuide />} />
          <Route path="weather/tide-guide" element={<TideGuide />} />
          <Route path="weather/alerts-guide" element={<WeatherAlertsGuide />} />
          <Route path="weather/trends-guide" element={<WeatherTrendsGuide />} />
          <Route path="weather/storm-approach-trend-guide" element={<StormApproachTrendGuide />} />
          {[...new Set(Object.values(localizedRoutes.weather).filter(Boolean))].map((weatherSlug) => (
            <React.Fragment key={`localized-weather-guides-${weatherSlug}`}>
              <Route path={`${weatherSlug}/wind-guide`} element={<WindGuide />} />
              <Route path={`${weatherSlug}/beaufort-guide`} element={<BeaufortGuide />} />
              <Route path={`${weatherSlug}/cloud-coverage-guide`} element={<CloudCoverageGuide />} />
              <Route path={`${weatherSlug}/air-pressure-guide`} element={<AirPressureGuide />} />
              <Route path={`${weatherSlug}/uv-index-guide`} element={<UVIndexGuide />} />
              <Route path={`${weatherSlug}/relative-humidity-guide`} element={<RelativeHumidityGuide />} />
              <Route path={`${weatherSlug}/sea-state-guide`} element={<SeaStateGuide />} />
              <Route path={`${weatherSlug}/visibility-guide`} element={<VisibilityGuide />} />
              <Route path={`${weatherSlug}/rain-guide`} element={<RainGuide />} />
              <Route path={`${weatherSlug}/sailing-decision-guide`} element={<SailingDecisionGuide />} />
              <Route path={`${weatherSlug}/sun-times-guide`} element={<SunTimesGuide />} />
              <Route path={`${weatherSlug}/temperature-guide`} element={<TemperatureGuide />} />
              <Route path={`${weatherSlug}/today-marine-forecast-guide`} element={<TodayMarineForecastGuide />} />
              <Route path={`${weatherSlug}/gusts-guide`} element={<WeatherGustsGuide />} />
              <Route path={`${weatherSlug}/score-guide`} element={<WeatherScoreGuide />} />
              <Route path={`${weatherSlug}/cloud-type-guide`} element={<CloudTypeGuide />} />
              <Route path={`${weatherSlug}/cloud-name-guide`} element={<CloudNameGuide />} />
              <Route path={`${weatherSlug}/max-wind-guide`} element={<MaxWindGuide />} />
              <Route path={`${weatherSlug}/max-waves-guide`} element={<MaxWavesGuide />} />
              <Route path={`${weatherSlug}/air-high-guide`} element={<AirHighGuide />} />
              <Route path={`${weatherSlug}/daily-forecast-guide`} element={<WeatherDailyForecastGuide />} />
              <Route path={`${weatherSlug}/tide-guide`} element={<TideGuide />} />
              <Route path={`${weatherSlug}/alerts-guide`} element={<WeatherAlertsGuide />} />
              <Route path={`${weatherSlug}/trends-guide`} element={<WeatherTrendsGuide />} />
              <Route path={`${weatherSlug}/storm-approach-trend-guide`} element={<StormApproachTrendGuide />} />
            </React.Fragment>
          ))}
          <Route path="weather/cloud-types" element={<CloudTypes />} />
          <Route path="weather/cloud-types/:cloudSlug" element={<CloudDetail />} />
          <Route path="marine-weather" element={<PublicIndexPage pageKey="weather" />} />
          <Route path="weather-forecast" element={<PublicIndexPage pageKey="weatherForecast" />} />
          <Route path="marine-weather/:country" element={<PublicIndexPage pageKey="weather" />} />
          <Route path="sailing-weather/:country" element={<PublicIndexPage pageKey="weather" />} />
          <Route path="wind-forecast/:country" element={<PublicIndexPage pageKey="weather" />} />
          <Route path="weather-forecast-for-sailing" element={<PublicIndexPage pageKey="weather" />} />
          <Route path="wind-forecast-for-boating" element={<PublicIndexPage pageKey="weather" />} />
          <Route path="sea-weather-forecast" element={<PublicIndexPage pageKey="weather" />} />
          <Route path="marinas" element={<LocalizedLegacyRoute routeKey="marinas"><PublicIndexPage pageKey="marinas" /></LocalizedLegacyRoute>} />
          {[...new Set(Object.values(localizedRoutes.marinas).filter((slug) => slug && slug !== "marinas"))].map((slug) => (
            <Route key={`marinas-index-${slug}`} path={slug} element={<PublicIndexPage pageKey="marinas" />} />
          ))}
          <Route path="marinas/:country" element={<PublicIndexPage pageKey="marinas" />} />
          <Route path="marinas/:country/:slug" element={<PublicMarinaDetailPage />} />
          <Route path="marina/:slug" element={<MarinaLegacyRedirect />} />
          <Route path="destinations" element={<PublicIndexPage pageKey="destinations" />} />
          <Route path="destinations/:country" element={<PublicIndexPage pageKey="destinations" />} />
          <Route path="sailing-calculators" element={<PublicIndexPage pageKey="tools" />} />
          <Route path="sailing-calculators/:toolSlug" element={<ToolPage />} />
          <Route path="learn" element={<Navigate to="../learning-center" replace />} />
          <Route path="learn/:topic" element={<LearnRedirect />} />
          <Route path="learn/:topic/:articleSlug" element={<LearnRedirect />} />
          <Route path="learning-center" element={<PublicIndexPage pageKey="learn" />} />
          <Route path="learning-center/:topic" element={<PublicIndexPage pageKey="learn" />} />
          <Route path="learning-center/:topic/:articleSlug" element={<PublicLearningArticlePage />} />
          {[...new Set(Object.values(localizedRoutes.learningCenter).filter((slug) => slug && slug !== "learning-center"))].map((slug) => (
            <React.Fragment key={`learning-center-${slug}`}>
              <Route path={`${slug}/:topic`} element={<PublicIndexPage pageKey="learn" />} />
              <Route path={`${slug}/:topic/:articleSlug`} element={<PublicLearningArticlePage />} />
            </React.Fragment>
          ))}
          <Route path="wire" element={<WireIndex />} />
          <Route path="wire/:slug" element={<WireArticlePage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="menu" element={<MobileMenuPage />} />
          <Route path="community" element={<LocalizedLegacyRoute routeKey="community"><Community /></LocalizedLegacyRoute>} />
          {[...new Set(Object.values(localizedRoutes.community).filter((slug) => slug && slug !== "community"))].map((slug) => (
            <Route key={`community-${slug}`} path={slug} element={<Community />} />
          ))}
          <Route path="affiliate" element={<LocalizedLegacyRoute routeKey="affiliate"><Affiliate /></LocalizedLegacyRoute>} />
          {[...new Set(Object.values(localizedRoutes.affiliate).filter((slug) => slug && slug !== "affiliate"))].map((slug) => (
            <Route key={`affiliate-${slug}`} path={slug} element={<Affiliate />} />
          ))}
          <Route path="search" element={<SearchPage />} />
          <Route path="billing" element={<Billing />} />
          <Route path="trips" element={<LocalizedLegacyRoute routeKey="trips"><TripPlanner /></LocalizedLegacyRoute>} />
          {[...new Set(Object.values(localizedRoutes.trips).filter((slug) => slug && slug !== "trips"))].map((slug) => (
            <Route key={`trips-${slug}`} path={slug} element={<TripPlanner />} />
          ))}
          {[...new Set(Object.values(localizedRoutes.premium).filter(Boolean))].map((slug) => (
            <Route key={`premium-${slug}`} path={slug} element={<Billing />} />
          ))}
          <Route path="marinas-near-me" element={<NearMePage type="marinas" />} />
          <Route path="anchorages-near-me" element={<NearMePage type="anchorages" />} />
          <Route path="fuel-near-me" element={<NearMePage type="fuel" />} />
          <Route path="marine-weather-near-me" element={<NearMePage type="weather" />} />
          <Route path="boat-services-near-me" element={<NearMePage type="services" />} />
          <Route path="routes" element={<SafeRoute><RoutesHub /></SafeRoute>} />
          <Route path="ports" element={<PortsLegacyRedirect />} />
          <Route path="ports/:country" element={<PortsLegacyRedirect />} />
          <Route path="ports/:country/:port" element={<PortsLegacyRedirect />} />
          <Route path="regulations" element={<SafeRoute><RegulationsHub /></SafeRoute>} />
          <Route path="sailing-regulations/:slug" element={<SeoGrowthPage group="sailing-regulations" />} />
          <Route path="marina-comparisons/:slug" element={<SeoGrowthPage group="marina-comparisons" />} />
          <Route path="weather-guides/:slug" element={<SeoGrowthPage group="weather-guides" />} />
          <Route path="sailing-checklists/:slug" element={<SeoGrowthPage group="sailing-checklists" />} />
          <Route path="sailing-locations/:slug" element={<SeoGrowthPage group="sailing-locations" />} />
          <Route path="glossary" element={<SafeRoute><GlossaryHub /></SafeRoute>} />
          <Route path="glossary/colregs" element={<SeoGrowthPage group="nautical-terms" slugOverride="colregs" />} />
          <Route path="glossary/epirb" element={<SeoGrowthPage group="nautical-terms" slugOverride="epirb" />} />
          <Route path="glossary/ais" element={<SeoGrowthPage group="nautical-terms" slugOverride="ais" />} />
          <Route path="glossary/vhf" element={<SeoGrowthPage group="nautical-terms" slugOverride="vhf" />} />
          <Route path="glossary/draught" element={<SeoGrowthPage group="nautical-terms" slugOverride="draught" />} />
          <Route path="glossary/mooring" element={<SeoGrowthPage group="nautical-terms" slugOverride="mooring" />} />
          <Route path="glossary/pilotage" element={<SeoGrowthPage group="nautical-terms" slugOverride="pilotage" />} />
          <Route path="glossary/passage-planning" element={<SeoGrowthPage group="nautical-terms" slugOverride="passage-planning" />} />
          {[...new Set(Object.values(localizedRoutes.radar).filter(Boolean))].map((slug) => (
            <Route key={`radar-${slug}`} path={slug} element={<PublicIndexPage pageKey="radar" />} />
          ))}
          {[...new Set(Object.values(localizedRoutes.trainingCenter).filter(Boolean))].map((slug) => (
            <Route key={`training-center-${slug}`} path={slug} element={<PublicIndexPage pageKey="learn" />} />
          ))}
          {[...new Set(Object.values(localizedRoutes.skipperTools).filter(Boolean))].map((slug) => (
            <Route key={`skipper-tools-${slug}`} path={slug} element={<PublicIndexPage pageKey="tools" />} />
          ))}
          {[...new Set(Object.values(localizedRoutes.editorialPolicy).filter(Boolean))].map((slug) => (
            <Route key={`editorial-policy-${slug}`} path={slug} element={<TrustPolicyPage policySlug="editorial-policy" />} />
          ))}
          {[...new Set(Object.values(localizedRoutes.navigation).filter(Boolean))].map((slug) => (
            <Route key={`navigation-${slug}`} path={slug} element={<LocalizedNavigationRoute />} />
          ))}
          {[...new Set(Object.values(localizedRoutes.businessTier).filter(Boolean))].map((slug) => (
            <Route key={`business-tier-${slug}`} path={slug} element={<MarstickBusinessTier />} />
          ))}
          {[...new Set(Object.values(localizedRoutes.fleetOperations).filter(Boolean))].map((slug) => (
            <Route key={`fleet-operations-${slug}`} path={slug} element={<FleetOperations />} />
          ))}
          {[...new Set(Object.values(localizedRoutes.aisCollisionCenter).filter(Boolean))].map((slug) => (
            <Route key={`ais-collision-center-${slug}`} path={slug} element={<AISCollisionCenter />} />
          ))}
          {[...new Set(Object.values(localizedRoutes.boatDocumentVault).filter(Boolean))].map((slug) => (
            <Route key={`boat-document-vault-${slug}`} path={slug} element={<BoatDocumentVault />} />
          ))}
          <Route path="offline" element={<OfflineNavigation />} />
          <Route path="training-center" element={<PublicIndexPage pageKey="learn" />} />
          <Route path="skipper-tools" element={<PublicIndexPage pageKey="tools" />} />
          <Route path="safety/pre-departure-checklist" element={<SafetyChecklistGuide />} />
          <Route path="safety/equipment-requirements" element={<SafetyEquipmentGuide />} />
          <Route path="downloads" element={<DownloadsHub />} />
          <Route path="advertise" element={<Advertise />} />
          <Route path="about" element={<TrustPolicyPage policySlug="about" />} />
          <Route path="privacy" element={<Privacy />} />
          <Route path="terms" element={<TermsAndConditions />} />
          <Route path="contact" element={<Contact />} />
          <Route path="cookies" element={<CookiePolicy />} />
          <Route path="cookie-policy" element={<CookiePolicy />} />
          <Route path="advertising-policy" element={<TrustPolicyPage policySlug="advertising-policy" />} />
          <Route path="editorial-policy" element={<TrustPolicyPage policySlug="editorial-policy" />} />
          <Route path="disclaimer" element={<TrustPolicyPage policySlug="disclaimer" />} />
          <Route path="safety-disclaimer" element={<TrustPolicyPage policySlug="safety-disclaimer" />} />
          <Route path="port-entry" element={<PortEntryPack />} />
          {[...new Set(Object.values(localizedRoutes.tools).filter(Boolean))].map((slug) => (
            <Route key={`localized-tools-detail-${slug}`} path={`${slug}/:toolSlug`} element={<ToolPage />} />
          ))}
          {[...new Set(Object.values(localizedRoutes.marinas).filter(Boolean))].map((slug) => (
            <React.Fragment key={`localized-marinas-deep-${slug}`}>
              <Route path={`${slug}/:country`} element={<PublicIndexPage pageKey="marinas" />} />
              <Route path={`${slug}/:country/:marinaSlug`} element={<PublicMarinaDetailPage />} />
            </React.Fragment>
          ))}
          {[...new Set(Object.values(localizedRoutes.destinations).filter(Boolean))].map((slug) => (
            <Route key={`localized-destinations-deep-${slug}`} path={`${slug}/:country`} element={<PublicIndexPage pageKey="destinations" />} />
          ))}
          {[...new Set(Object.values(localizedRoutes.weather).filter(Boolean))].map((slug) => (
            <Route key={`localized-weather-spot-${slug}`} path={`${slug}/:spotSlug`} element={<WeatherSpotPage />} />
          ))}
          {[...new Set(Object.values(localizedRoutes.routes).filter(Boolean))].map((slug) => (
            <React.Fragment key={`localized-routes-${slug}`}>
              <Route path={slug} element={<SafeRoute><RoutesHub /></SafeRoute>} />
              <Route path={`${slug}/:country/:slug`} element={<SafeRoute><RouteDetailPage /></SafeRoute>} />
            </React.Fragment>
          ))}
          {[...new Set(Object.values(localizedRoutes.regulations).filter(Boolean))].map((slug) => (
            <Route key={`localized-regulations-detail-${slug}`} path={`${slug}/:country/:topic`} element={<SafeRoute><RegulationDetailPage /></SafeRoute>} />
          ))}
          {[...new Set(Object.values(localizedRoutes.glossary).filter(Boolean))].map((slug) => (
            <Route key={`localized-glossary-detail-${slug}`} path={`${slug}/:term`} element={<SafeRoute><GlossaryTermPage /></SafeRoute>} />
          ))}
          {[...new Set(Object.values(localizedRoutes.wire).filter(Boolean))].map((slug) => (
            <Route key={`localized-wire-detail-${slug}`} path={`${slug}/:slug`} element={<WireArticlePage />} />
          ))}
          <Route path="learning/:category/:slug" element={<LearningArticleRedirect />} />
          <Route path=":slug" element={<LocalizedPublicPage />} />
        </Route>

        <Route element={<PublicLayout />}>
          <Route path="/" element={<PublicHome />} />
          <Route path="/home" element={<Navigate to="/" replace />} />
          <Route path="/Home" element={<Navigate to="/" replace />} />
          <Route path="/wire" element={<WireIndex />} />
          <Route path="/daily-briefing" element={<DailyBriefingPage />} />
          <Route path="/install" element={<InstallGuidePage />} />
          <Route path="/extensions" element={<ExtensionsPage />} />
          <Route path="/extensions/enterprise" element={<ExtensionsEnterprisePage />} />
          <Route path="/extensions/:browserSlug" element={<ExtensionsPage />} />
          <Route path="/community" element={<Community />} />
          <Route path="/wire/:slug" element={<WireArticlePage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/menu" element={<MobileMenuPage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/premium" element={<Billing />} />
          <Route path="/weather" element={<PublicIndexPage pageKey="weather" />} />
          <Route path="/marinas" element={<PublicIndexPage pageKey="marinas" />} />
          <Route path="/sailing-calculators" element={<PublicIndexPage pageKey="tools" />} />
          <Route path="/sailing-calculators/:toolSlug" element={<ToolPage />} />
          <Route path="/learn" element={<BrowserLanguageNavigate routeKey="learningCenter" />} />
          <Route path="/learn/:topic" element={<LearnRedirect />} />
          <Route path="/learn/:topic/:articleSlug" element={<LearnRedirect />} />
          <Route path="/learning-center" element={<PublicIndexPage pageKey="learn" />} />
          <Route path="/learning-center/:topic" element={<PublicIndexPage pageKey="learn" />} />
          <Route path="/learning-center/:topic/:articleSlug" element={<PublicLearningArticlePage />} />
          <Route path="/marine-weather" element={<PublicIndexPage pageKey="weather" />} />
          <Route path="/weather-forecast" element={<PublicIndexPage pageKey="weatherForecast" />} />
          <Route path="/marine-weather/:country" element={<PublicIndexPage pageKey="weather" />} />
          <Route path="/sailing-weather/:country" element={<PublicIndexPage pageKey="weather" />} />
          <Route path="/wind-forecast/:country" element={<PublicIndexPage pageKey="weather" />} />
          <Route path="/weather-forecast-for-sailing" element={<PublicIndexPage pageKey="weather" />} />
          <Route path="/wind-forecast-for-boating" element={<PublicIndexPage pageKey="weather" />} />
          <Route path="/sea-weather-forecast" element={<PublicIndexPage pageKey="weather" />} />
          <Route path="/destinations" element={<PublicIndexPage pageKey="destinations" />} />
          <Route path="/destinations/:country" element={<PublicIndexPage pageKey="destinations" />} />
          <Route path="/marina/:slug" element={<MarinaLegacyRedirect />} />
          <Route path="/marinas/:country" element={<PublicIndexPage pageKey="marinas" />} />
          <Route path="/marinas/:country/:slug" element={<PublicMarinaDetailPage />} />
          <Route path="/marinas-near-me" element={<NearMePage type="marinas" />} />
          <Route path="/anchorages-near-me" element={<NearMePage type="anchorages" />} />
          <Route path="/fuel-near-me" element={<NearMePage type="fuel" />} />
          <Route path="/marine-weather-near-me" element={<NearMePage type="weather" />} />
          <Route path="/boat-services-near-me" element={<NearMePage type="services" />} />
          <Route path="/about" element={<TrustPolicyPage policySlug="about" />} />
          <Route path="/cookies" element={<CookiePolicy />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/advertising-policy" element={<TrustPolicyPage policySlug="advertising-policy" />} />
          <Route path="/editorial-policy" element={<TrustPolicyPage policySlug="editorial-policy" />} />
          <Route path="/disclaimer" element={<TrustPolicyPage policySlug="disclaimer" />} />
          <Route path="/safety-disclaimer" element={<TrustPolicyPage policySlug="safety-disclaimer" />} />
          <Route path="/routes" element={<SafeRoute><RoutesHub /></SafeRoute>} />
          <Route path="/ports" element={<PortsLegacyRedirect />} />
          <Route path="/ports/:country" element={<PortsLegacyRedirect />} />
          <Route path="/ports/:country/:port" element={<PortsLegacyRedirect />} />
          <Route path="/regulations" element={<SafeRoute><RegulationsHub /></SafeRoute>} />
          <Route path="/sailing-regulations/:slug" element={<SeoGrowthPage group="sailing-regulations" />} />
          <Route path="/marina-comparisons/:slug" element={<SeoGrowthPage group="marina-comparisons" />} />
          <Route path="/weather-guides/:slug" element={<SeoGrowthPage group="weather-guides" />} />
          <Route path="/sailing-checklists/:slug" element={<SeoGrowthPage group="sailing-checklists" />} />
          <Route path="/sailing-locations/:slug" element={<SeoGrowthPage group="sailing-locations" />} />
          <Route path="/glossary" element={<SafeRoute><GlossaryHub /></SafeRoute>} />
          <Route path="/glossary/colregs" element={<SeoGrowthPage group="nautical-terms" slugOverride="colregs" />} />
          <Route path="/glossary/epirb" element={<SeoGrowthPage group="nautical-terms" slugOverride="epirb" />} />
          <Route path="/glossary/ais" element={<SeoGrowthPage group="nautical-terms" slugOverride="ais" />} />
          <Route path="/glossary/vhf" element={<SeoGrowthPage group="nautical-terms" slugOverride="vhf" />} />
          <Route path="/glossary/draught" element={<SeoGrowthPage group="nautical-terms" slugOverride="draught" />} />
          <Route path="/glossary/mooring" element={<SeoGrowthPage group="nautical-terms" slugOverride="mooring" />} />
          <Route path="/glossary/pilotage" element={<SeoGrowthPage group="nautical-terms" slugOverride="pilotage" />} />
          <Route path="/glossary/passage-planning" element={<SeoGrowthPage group="nautical-terms" slugOverride="passage-planning" />} />
          <Route path="/business-tier" element={<MarstickBusinessTier />} />
          <Route path="/fleet-operations" element={<FleetOperations />} />
          <Route path="/ais-collision-center" element={<AISCollisionCenter />} />
          <Route path="/boat-document-vault" element={<BoatDocumentVault />} />
        </Route>

        {/* ADMIN */}
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="users" element={<UserManagement />} />
          <Route path="analytics" element={<AdminAnalytics />} />
          <Route path="seo" element={<SeoMetaManager />} />
          <Route path="seo-dashboard" element={<SEODashboard />} />
          <Route path="seo-meta" element={<SeoMetaSettings />} />
          <Route path="sitemap" element={<SitemapVerifier />} />
          <Route path="notifications" element={<AdminNotifications />} />
          <Route path="educational-content" element={<EducationalContentManager />} />
          <Route path="learning-content" element={<LearningContentManager />} />
          <Route path="content-quality" element={<ContentQualityDashboard />} />
          <Route path="global-search-terms" element={<GlobalSearchTerms />} />
          <Route path="marina-duplicates" element={<MarinaDuplicateAgent />} />
          <Route path="advertising" element={<AdManager />} />
          <Route path="advertising/performance" element={<AdPerformanceDashboard />} />
          <Route path="advertising/adsense-test" element={<AdSenseTest />} />
          <Route path="system-health" element={<SystemHealth />} />
          <Route path="premium-segments" element={<PremiumSegments />} />
          <Route path="affiliate-program" element={<AdminAffiliateProgram />} />
          <Route path="welcome-editor" element={<WelcomeContentEditor />} />
          <Route path="advertising/content" element={<AdvertisingContentEditor />} />
          <Route path="support-chat" element={<SupportChat />} />
          <Route path="payment-solutions" element={<PaymentSolutions />} />
          <Route path="payment-history" element={<PaymentHistory />} />
          <Route path="churn-monitor" element={<ChurnRiskMonitor />} />
          <Route path="navigation-visibility" element={<NavigationVisibility />} />
          <Route path="tracking-settings" element={<TrackingSettings />} />
          <Route path="translation-center" element={<TranslationCenter />} />
          <Route path="revenue-growth" element={<RevenueGrowth />} />
          <Route path="backup" element={<BackupExport />} />
          <Route path="social-media" element={<SocialMediaManager />} />
          <Route path="backlinks" element={<BacklinkManager />} />
          <Route path="revenue" element={<RevenueGrowth />} />
          <Route path="churn" element={<ChurnRiskMonitor />} />
          <Route path="backup-export" element={<BackupExport />} />

          <Route path="welcome-content" element={<WelcomeContentEditor />} />
          <Route path="bi" element={<AIBusinessIntelligence />} />
          <Route path="qa-reports" element={<QAReports />} />
          <Route path="youtube-scripts" element={<YouTubeScriptGenerator />} />
          <Route path="tools/:tool" element={<AdminToolPage />} />
          <Route path="browser-extensions" element={<BrowserExtensions />} />
          <Route path="ads" element={<Navigate to="/admin/advertising" replace />} />
          <Route path="ads/performance" element={<Navigate to="/admin/advertising/performance" replace />} />
          <Route path="ads/test" element={<Navigate to="/admin/advertising/adsense-test" replace />} />
          <Route path="ad-manager" element={<Navigate to="/admin/advertising" replace />} />
          <Route path="ad-performance" element={<Navigate to="/admin/advertising/performance" replace />} />
          <Route path="advertising-content" element={<Navigate to="/admin/advertising/content" replace />} />
        </Route>

        <Route element={<AppLayout />}>

          {/* Redirects & Direct Routes */}
          <Route path="/navigation" element={<BrowserLanguageNavigate routeKey="navigation" />} />
          <Route path="/weather" element={<BrowserLanguageNavigate routeKey="weather" />} />
          <Route path="/weather/wind-guide" element={<SafeRoute><WindGuide /></SafeRoute>} />
          <Route path="/weather/beaufort-guide" element={<SafeRoute><BeaufortGuide /></SafeRoute>} />
          <Route path="/weather/cloud-coverage-guide" element={<SafeRoute><CloudCoverageGuide /></SafeRoute>} />
          <Route path="/weather/air-pressure-guide" element={<SafeRoute><AirPressureGuide /></SafeRoute>} />
          <Route path="/weather/uv-index-guide" element={<SafeRoute><UVIndexGuide /></SafeRoute>} />
          <Route path="/weather/relative-humidity-guide" element={<SafeRoute><RelativeHumidityGuide /></SafeRoute>} />
          <Route path="/weather/sea-state-guide" element={<SafeRoute><SeaStateGuide /></SafeRoute>} />
          <Route path="/weather/visibility-guide" element={<SafeRoute><VisibilityGuide /></SafeRoute>} />
          <Route path="/weather/rain-guide" element={<SafeRoute><RainGuide /></SafeRoute>} />
          <Route path="/weather/sailing-decision-guide" element={<SafeRoute><SailingDecisionGuide /></SafeRoute>} />
          <Route path="/weather/sun-times-guide" element={<SafeRoute><SunTimesGuide /></SafeRoute>} />
          <Route path="/weather/temperature-guide" element={<SafeRoute><TemperatureGuide /></SafeRoute>} />
          <Route path="/weather/today-marine-forecast-guide" element={<SafeRoute><TodayMarineForecastGuide /></SafeRoute>} />
          <Route path="/weather/gusts-guide" element={<SafeRoute><WeatherGustsGuide /></SafeRoute>} />
          <Route path="/weather/score-guide" element={<SafeRoute><WeatherScoreGuide /></SafeRoute>} />
          <Route path="/weather/cloud-type-guide" element={<SafeRoute><CloudTypeGuide /></SafeRoute>} />
          <Route path="/weather/cloud-name-guide" element={<SafeRoute><CloudNameGuide /></SafeRoute>} />
          <Route path="/weather/max-wind-guide" element={<SafeRoute><MaxWindGuide /></SafeRoute>} />
          <Route path="/weather/max-waves-guide" element={<SafeRoute><MaxWavesGuide /></SafeRoute>} />
          <Route path="/weather/air-high-guide" element={<SafeRoute><AirHighGuide /></SafeRoute>} />
          <Route path="/weather/daily-forecast-guide" element={<SafeRoute><WeatherDailyForecastGuide /></SafeRoute>} />
          <Route path="/weather/tide-guide" element={<SafeRoute><TideGuide /></SafeRoute>} />
          <Route path="/weather/alerts-guide" element={<SafeRoute><WeatherAlertsGuide /></SafeRoute>} />
          <Route path="/weather/trends-guide" element={<SafeRoute><WeatherTrendsGuide /></SafeRoute>} />
          <Route path="/weather/storm-approach-trend-guide" element={<SafeRoute><StormApproachTrendGuide /></SafeRoute>} />
          <Route path="/weather/cloud-types" element={<SafeRoute><CloudTypes /></SafeRoute>} />
          <Route path="/weather/cloud-types/:cloudSlug" element={<SafeRoute><CloudDetail /></SafeRoute>} />
          <Route path="/weather/:spotSlug" element={<SafeRoute><WeatherSpotPage /></SafeRoute>} />
          <Route path="/surf/:spotSlug" element={<SafeRoute><WeatherSpotPage /></SafeRoute>} />
          <Route path="/sailing/:spotSlug" element={<SafeRoute><WeatherSpotPage /></SafeRoute>} />
          <Route path="/safety" element={<BrowserLanguageNavigate routeKey="safety" />} />
          <Route path="/marinas" element={<BrowserLanguageNavigate routeKey="marinas" />} />
          <Route path="/radar" element={<BrowserLanguageNavigate routeKey="radar" />} />
          <Route path="/route-library" element={<BrowserLanguageNavigate routeKey="routes" />} />
          <Route path="/dashboard" element={<Dashboard />} />
          {[...new Set(Object.values(localizedRoutes.dashboard).filter((slug) => slug && slug !== "dashboard"))].map((slug) => (
            <Route key={`dashboard-${slug}`} path={`/:lang/${slug}`} element={<Dashboard />} />
          ))}
          <Route path="/boats" element={<SafeRoute><MyFleet /></SafeRoute>} />
          {[...new Set(Object.values(localizedRoutes.boats).filter(Boolean))].map((slug) => (
            <Route key={`boats-${slug}`} path={`/:lang/${slug}`} element={<SafeRoute><MyFleet /></SafeRoute>} />
          ))}
          <Route path="/maintenance" element={<SafeRoute><Maintenance /></SafeRoute>} />
          <Route path="/:lang/maintenance" element={<LocalizedLegacyRoute routeKey="maintenance"><SafeRoute><Maintenance /></SafeRoute></LocalizedLegacyRoute>} />
          {[...new Set(Object.values(localizedRoutes.maintenance).filter((slug) => slug && slug !== "maintenance"))].map((slug) => (
            <Route key={`maintenance-${slug}`} path={`/:lang/${slug}`} element={<SafeRoute><Maintenance /></SafeRoute>} />
          ))}
          <Route path="/:lang/trips" element={<LocalizedLegacyRoute routeKey="trips"><TripPlanner /></LocalizedLegacyRoute>} />
          {[...new Set(Object.values(localizedRoutes.trips).filter((slug) => slug && slug !== "trips"))].map((slug) => (
            <Route key={`trips-${slug}`} path={`/:lang/${slug}`} element={<TripPlanner />} />
          ))}
          <Route path="/:lang/affiliate" element={<LocalizedLegacyRoute routeKey="affiliate"><Affiliate /></LocalizedLegacyRoute>} />
          <Route path="/:lang/voyage-history" element={<LocalizedRouteRedirect routeKey="metrics" />} />
          {[...new Set(Object.values(localizedRoutes.voyageHistory).filter((slug) => slug && slug !== "voyage-history"))].map((slug) => (
            <Route key={`voyage-history-${slug}`} path={`/:lang/${slug}`} element={<LocalizedRouteRedirect routeKey="metrics" />} />
          ))}
          <Route path="/voyage-history" element={<Navigate to="/metrics" replace />} />
          <Route path="/education" element={<BrowserLanguageNavigate routeKey="learningCenter" />} />
          <Route path="/education/:section/:slug" element={<TopicPage />} />
          <Route path="/training-center" element={<BrowserLanguageNavigate routeKey="trainingCenter" />} />

          {/*
            NEW: bare /learning/:category → /en/learning-center?category=...
            MUST appear BEFORE the 3-segment /learning/:category/:slug route.
            Fixes Google Search Console soft-404 / "Page with redirect" reports
            on category-only learning URLs like /learning/navigation-positioning.
          */}
          <Route path="/learning/:category" element={<LearningCategoryRedirect />} />
          <Route path="/learning/:category/:slug" element={<LearningArticleRedirect />} />
          <Route path="/learning-center/:category/:slug" element={<LearningArticleRouteGuard />} />
          <Route path="/404" element={<MissingRoute />} />

          <Route path="/metrics" element={<Metrics />} />
          {[...new Set(Object.values(localizedRoutes.metrics).filter((slug) => slug && slug !== "metrics"))].map((slug) => (
            <Route key={`metrics-${slug}`} path={`/:lang/${slug}`} element={<Metrics />} />
          ))}
          <Route path="/logbook" element={<SafeRoute><Logbook /></SafeRoute>} />
          <Route path="/:lang/logbook" element={<SafeRoute><Logbook /></SafeRoute>} />
          {[...new Set(Object.values(localizedRoutes.logbook).filter((slug) => slug && slug !== "logbook"))].map((slug) => (
            <Route key={`logbook-${slug}`} path={`/:lang/${slug}`} element={<SafeRoute><Logbook /></SafeRoute>} />
          ))}
          <Route path="/offline" element={<OfflineNavigation />} />
          {[...new Set(Object.values(localizedRoutes.offline).filter((slug) => slug && slug !== "offline"))].map((slug) => (
            <Route key={`offline-${slug}`} path={`/:lang/${slug}`} element={<OfflineNavigation />} />
          ))}
          <Route path="/premium-operations" element={<BrowserLanguageNavigate routeKey="fleetOperations" />} />
          <Route path="/chart-plotter" element={<BrowserLanguageNavigate routeKey="navigation" />} />
          <Route path="/advanced-voyage-planner" element={<BrowserLanguageNavigate routeKey="navigation" />} />
          <Route path="/help" element={<HelpCenter />} />
          {[...new Set(Object.values(localizedRoutes.help).filter((slug) => slug && slug !== "help"))].map((slug) => (
            <Route key={`help-${slug}`} path={`/:lang/${slug}`} element={<HelpCenter />} />
          ))}
          <Route path="/skipper-tools" element={<BrowserLanguageNavigate routeKey="skipperTools" />} />
          <Route path="/trips" element={<TripPlanner />} />
          <Route path="/affiliate" element={<Affiliate />} />
          <Route path="/port-entry" element={<BrowserLanguageNavigate routeKey="portEntry" />} />
          <Route path="/routes/:country/:slug" element={<SafeRoute><RouteDetailPage /></SafeRoute>} />
          <Route path="/regulations/:country/:topic" element={<SafeRoute><RegulationDetailPage /></SafeRoute>} />
          <Route path="/glossary/:term" element={<SafeRoute><GlossaryTermPage /></SafeRoute>} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/feedback" element={<BrowserLanguageNavigate routeKey="feedback" />} />
          <Route path="/advertise" element={<BrowserLanguageNavigate routeKey="advertise" />} />

          <Route path="/settings" element={<Settings />} />
          {[...new Set(Object.values(localizedRoutes.settings).filter((slug) => slug && slug !== "settings"))].map((slug) => (
            <Route key={`settings-${slug}`} path={`/:lang/${slug}`} element={<Settings />} />
          ))}
          <Route path="/billing" element={<Billing />} />
          {[...new Set(Object.values(localizedRoutes.billing).filter((slug) => slug && slug !== "billing"))].map((slug) => (
            <Route key={`billing-${slug}`} path={`/:lang/${slug}`} element={<Billing />} />
          ))}
          <Route path="/billing/paypal-return" element={<PayPalReturn />} />
          <Route path="/billing/success" element={<SubscriptionSuccess />} />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/log-analytics" element={<Navigate to="/metrics" replace />} />
          <Route path="/user-flow" element={<BrowserLanguageNavigate routeKey="home" />} />

          <Route path="*" element={<MissingRoute />} />
        </Route>

        <Route path="/landing" element={<Landing />} />
      </Routes>
      </Suspense>
    </>
  );
};

const DeferredReferralAttribution = () => {
  const ready = useDeferredIdleRender(2600);
  if (!ready) return null;
  return (
    <Suspense fallback={null}>
      <ReferralAttributionCapture />
    </Suspense>
  );
};

const InitialShellCleanup = () => {
  React.useEffect(() => {
    document.documentElement.classList.remove("marstick-show-initial-shell");
    document.getElementById("marstick-initial-shell")?.remove();
  }, []);

  return null;
};

const AppBrowserCacheCleaner = () => {
  React.useEffect(() => {
    const clearVersion = "2026-07-13-public-index-dynamic-import-refresh";
    const markerKey = "marstick_cache_clear_version";

    if (localStorage.getItem(markerKey) === clearVersion) return;

    const preservedKeys = new Set([markerKey, "marstick_cookie_consent_v2", "marstick_cookie_preferences_seen_v1", "marstick_theme", "theme"]);
    Object.keys(localStorage).forEach((key) => {
      if (!preservedKeys.has(key)) localStorage.removeItem(key);
    });
    sessionStorage.clear();
    localStorage.setItem(markerKey, clearVersion);

    if ("caches" in window) {
      caches.keys().then((keys) => keys.forEach((key) => caches.delete(key)));
    }
  }, []);

  return null;
};

function App() {
  return (
    <ThemeProvider>
      <ConsolePrivacyGuard />
      <AuthProvider>
        <LanguageProvider>
          <OfflineProvider>
            <QueryClientProvider client={queryClientInstance}>
              <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                <FormAttributeDefaults />
                <InitialShellCleanup />
                <AppBrowserCacheCleaner />
                <DeferredReferralAttribution />
                <ScrollToTop />
                <LanguagePreferenceNavigator />
                <HardcodedTextLocalizer />
                <AuthenticatedApp />
              </Router>
              <Toaster />
            </QueryClientProvider>
          </OfflineProvider>
        </LanguageProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;